// Apis/quizApi.ts
import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { answerModel, questionModel } from "../Interfaces/QuestionModel";

// DTO Interfaces

export interface AnswerSubmissionDTO {
  questionId: number;
  selectedAnswerId: number;
}

export interface CompleteQuizWithAnswersRequest {
  progressId: number;
  answers: AnswerSubmissionDTO[];
}

export interface QuizProgress {
  id: number;
  userId: string;
  quizId: number;
  score: number;
  startedAt: string;
  completedAt?: string;
  isCompleted: boolean;
  timeTaken: string;
  userAnswers?: UserQuizAnswer[];
  quiz?: Quiz;
}

export interface UserQuizAnswer {
  id: number;
  userQuizProgressId: number;
  questionId: number;
  selectedAnswerId: number;
  isCorrect: boolean;
  answeredAt: string;
  question?: questionModel;
  selectedAnswer?: answerModel;
}

export interface Quiz {
  id: number;
  name: string;
  description: string;
  startDate: string;
  endDate: string;
}
export interface TopTen {
  userId: string;
  username: string;
  fullName: string;
  entity: string;
  totalScore: number;
  quizzesTaken: number;
  lastCompleted: string;
  rank: number;
}

export interface LeaderboardResponse {
  topTen: TopTen[];
  allParticipants: TopTen[];
  currentUserRank: number;
  totalParticipants: number;
  highestScore: number;
  currentUserScore: number;
}
export interface SubmitRatingRequest {
  progressId: number;
  rate: number;
  comment?: string;
}

export interface SubmitRatingResponse {
  message: string;
  progressId: number;
  rate: number;
  comment?: string;
}
export interface AddBonusRequest {
  progressId: number;
  didShare: boolean;
}
export interface AddBonusResponse {
  score: number;
  message: string;
}
export interface UserProgressReport {
  id: number;
  name: string;
  score: number;
  bonusAwarded: boolean;
}
export interface QuizAnalytics {
  questionText: string;
  correctAnswers: number;
  wrongAnswers: number;
  totalResponses: number;
}
export interface Ratings {
  averageRate: number;
  totalRaters: number;
}

export interface Comments {
  fullName: string;
  comment?: string;
  rate: number;
}

export const progressApi = createApi({
  reducerPath: "progressApi",
  baseQuery: fetchBaseQuery({
    baseUrl: "/api",
    prepareHeaders: (headers) => {
      const token = localStorage.getItem("token");
      if (token) {
        headers.set("Authorization", `Bearer ${token}`);
      }
      return headers;
    },
  }),
  tagTypes: ["Quiz", "Question", "Progress", "Leaderboard"],
  endpoints: (builder) => ({
    // Existing endpoints
    getNextQuiz: builder.query({
      query: (userId) => `Quiz/GetNextQuizForUser/${userId}`,
      providesTags: ["Quiz"],
    }),
    getQuizById: builder.query({
      query: (id) => `Quiz/GetQuizById/${id}`,
      providesTags: ["Quiz"],
    }),
    getQuestionsByQuizId: builder.query({
      query: (quizId) => `Question/ByQuiz/${quizId}`,
      providesTags: ["Question"],
    }),
    getLeaderboard: builder.query<LeaderboardResponse, void>({
      query: () => "Progress/GetLeaderboard",
      providesTags: ["Leaderboard"],
    }),

    // Progress Controller endpoints
    startQuiz: builder.mutation<QuizProgress, number>({
      query: (quizId) => ({
        url: `Progress/StartQuiz/${quizId}`,
        method: "POST",
      }),
      invalidatesTags: ["Progress"],
    }),

    completeQuizWithAnswers: builder.mutation<
      {
        score: number;
        timeFactor: number;
        finalScore: number;
        timeTaken: string;
        totalQuestions: number;
        correctAnswers: number;
      },
      CompleteQuizWithAnswersRequest
    >({
      query: (request) => ({
        url: "Progress/CompleteQuizWithAnswers",
        method: "POST",
        body: request,
      }),
      invalidatesTags: ["Progress"],
    }),

    getUserProgress: builder.query<QuizProgress[], void>({
      query: () => "Progress/GetUserProgress",
      providesTags: ["Progress"],
    }),

    getQuizResult: builder.query<QuizProgress, number>({
      query: (progressId) => `Progress/GetQuizResult/${progressId}`,
      providesTags: ["Progress"],
    }),
    submitRating: builder.mutation<SubmitRatingResponse, SubmitRatingRequest>({
      query: (payload) => ({
        url: "Progress/RateProgress",
        method: "POST",
        body: payload,
      }),
      invalidatesTags: ["Progress", "Leaderboard"],
    }),
    getUserProgressReport: builder.query<UserProgressReport[], void>({
      query: () => "Progress/UserProgressReport",
      providesTags: ["Progress"],
    }),
    GetQuizAnalytics: builder.query<QuizAnalytics[], number>({
      query: (quizId) => `Progress/QuizAnalytics/${quizId}`,
      providesTags: ["Progress"],
    }),
    GetQuizRating: builder.query<Ratings, number>({
      query: (quizId) => `Progress/QuizRating/${quizId}`,
      providesTags: ["Progress"],
    }),
    GetQuizComments: builder.query<Comments[], number>({
      query: (quizId) => `Progress/QuizComments/${quizId}`,
      providesTags: ["Progress"],
    }),
  }),
});

export const {
  useGetNextQuizQuery,
  useGetQuizByIdQuery,
  useGetQuestionsByQuizIdQuery,
  useGetLeaderboardQuery,
  useStartQuizMutation,
  useCompleteQuizWithAnswersMutation,
  useGetUserProgressQuery,
  useGetQuizResultQuery,
  useSubmitRatingMutation,
  useGetUserProgressReportQuery,
  useGetQuizAnalyticsQuery,
  useGetQuizRatingQuery,
  useGetQuizCommentsQuery,
} = progressApi;

export default progressApi;
